// Prompt construction (ARCHITECTURE.md §11.3 step 3).
//
// Renders a SourceContext into a single structured prompt string. The prompt
// states the gap being investigated, lays out each file's symbols and source
// snippets, and asks the LLM to return a strict JSON array of candidate
// relationships. The requested JSON shape mirrors `CandidateProposal[]` so the
// response parser can validate it directly.

import { EDGE_KINDS } from "../types.js";
import { EVIDENCE_TYPES } from "../evidence/types.js";
import type { FileContext, SourceContext } from "./types.js";

function renderFile(fc: FileContext): string {
  const lines: string[] = [`### File: ${fc.filePath}`];

  if (fc.symbols.length > 0) {
    lines.push("Symbols:");
    for (const s of fc.symbols) {
      const exported = s.exported ? "exported " : "";
      lines.push(`  - ${exported}${s.kind} ${s.name} (line ${s.line})`);
    }
  } else {
    lines.push("Symbols: (none extracted)");
  }

  if (fc.existingEdges.length > 0) {
    lines.push("Existing edges:");
    for (const e of fc.existingEdges) {
      lines.push(`  - ${e.kind} -> ${e.to} (${e.classification})`);
    }
  }

  if (fc.snippets.length > 0) {
    lines.push("Source snippets:");
    for (const snip of fc.snippets) {
      lines.push(
        `  [${snip.context}] lines ${snip.startLine}-${snip.endLine}:`,
      );
      lines.push("  ```");
      for (const line of snip.content.split("\n")) lines.push(`  ${line}`);
      lines.push("  ```");
    }
  }

  return lines.join("\n");
}

/**
 * Build the LLM prompt for one assembled SourceContext. Output is deterministic
 * for a given context, which keeps prompts cacheable and tests stable.
 */
export function buildPrompt(context: SourceContext): string {
  const { file, relatedFiles, gap } = context;

  const header = [
    "You are a source-code relationship analyzer.",
    "Two files may be related even though no import statement links them",
    "(for example: generated code and its schema, tests and implementation,",
    "or configuration loaded by string path).",
    "",
    "## Gap under investigation",
    `Reason: ${gap.reason}`,
    gap.contractRelation
      ? `Contract relation: ${gap.contractRelation}`
      : "Contract relation: (none)",
    `Heuristic confidence: ${gap.confidence}`,
    "",
    "## Primary file",
    renderFile(file),
    "",
    "## Related file(s)",
    ...relatedFiles.map(renderFile),
    "",
    "## Task",
    "Decide whether the primary file is related to any related file.",
    `Allowed edgeKind values: ${EDGE_KINDS.join(", ")}.`,
    `Allowed evidence type values: ${EVIDENCE_TYPES.join(", ")}.`,
    "",
    "Respond with ONLY a JSON array (no prose, no markdown fences) of objects:",
    "[",
    "  {",
    '    "from": "<file path>",',
    '    "to": "<file path>",',
    '    "edgeKind": "<one of the allowed edgeKind values>",',
    '    "confidence": <number between 0 and 1>,',
    '    "reasoning": "<short natural-language explanation>",',
    '    "evidencePoints": [',
    '      { "type": "<one of the allowed evidence types>", "detail": "<text>", "line": <optional 1-based line> }',
    "    ]",
    "  }",
    "]",
    "If there is no relationship, respond with an empty array: []",
  ];

  return header.join("\n");
}
